import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Global axios retry interceptor — retries failed GETs with exponential
// backoff. On the production POS the backend occasionally has transient
// 5xx / timeout blips (MongoDB Atlas replica-set failover, connection-
// pool contention, ingress hiccup); without this interceptor every one
// of those surfaces to the cashier as "Failed to load…". One retry
// wasn't enough during an Atlas election (~10–20 s). Two retries with
// 1.5 s / 4 s backoff covers almost every real-world case. Also retries
// on request timeouts (ECONNABORTED) and bare network errors (no
// response object) — same symptoms, same recovery.
//
// POST-RETRY MESSAGE REWRITE: if all retries exhaust, the generic
// backend "Internal server error. Please try again." text (set by
// server.py's global exception handler) still bubbles up to any
// `toast.error(err.response.data.detail)` call across the app and
// scares the cashier. Rewrite it to a less alarming, more actionable
// message AFTER retries exhaust so a single scary toast doesn't make
// a slow-DB blip look like a system crash.
const _MAX_RETRIES = 2;
const _RETRY_DELAYS_MS = [1500, 4000];
// Login POST endpoints that are safe to retry — the backend creates a
// new session doc per attempt (old sessions expire via TTL) so a
// retried login is idempotent in effect. Without this, a transient
// Atlas blip during login locks the cashier out even though a retry
// one second later would succeed.
const _RETRY_SAFE_POSTS = /\/api\/(auth\/login|auth\/pin-login|users\/login)(\?|$)/;
const _TRANSIENT_FRIENDLY = "Server is slow — please wait a moment and try again.";
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const retryCount = config?._retryCount || 0;
    const isTransient =
      !error.response ||
      error.response.status >= 500 ||
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK';
    const url = config?.url || '';
    const method = (config?.method || '').toLowerCase();
    const isRetryable =
      method === 'get' ||
      (method === 'post' && _RETRY_SAFE_POSTS.test(url));
    if (
      config &&
      retryCount < _MAX_RETRIES &&
      isRetryable &&
      isTransient
    ) {
      config._retryCount = retryCount + 1;
      await new Promise((r) => setTimeout(r, _RETRY_DELAYS_MS[retryCount] || 4000));
      return axios(config);
    }
    // Rewrite the generic backend 500 payload so cashiers see a
    // calming message instead of "Internal server error" on blips
    // that the retry couldn't swallow in time.
    if (
      isTransient &&
      error.response?.data?.detail &&
      /internal server error/i.test(String(error.response.data.detail))
    ) {
      try {
        error.response.data = {
          ...error.response.data,
          detail: _TRANSIENT_FRIENDLY,
          original_detail: error.response.data.detail,
        };
      } catch { /* best-effort rewrite */ }
    }
    return Promise.reject(error);
  }
);
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ReorderReport from "@/pages/ReorderReport";
import DataImport from "@/pages/DataImport";
import Sales from "@/pages/Sales";
import Quotations from "@/pages/Quotations";
import QuotationManagement from "@/pages/QuotationManagement";
import Customers from "@/pages/Customers";
import CustomersV2 from "@/pages/CustomersV2";
import ProductMaster from "@/pages/ProductMaster";
import PettyCashReport from "@/pages/PettyCashReport";
import StockReceiving from "@/pages/StockReceiving";
import StockConversions from "@/pages/StockConversions";
import BinAssign from "@/pages/BinAssign";
import BuyingGroupReport from "@/pages/BuyingGroupReport";
import PrintStation from "@/pages/PrintStation";
import InternalSupplies from "@/pages/InternalSupplies";
import MakroDirect from "@/pages/MakroDirect";
import Suppliers from "@/pages/Suppliers";
import LimpopoInsights from "@/pages/LimpopoInsights";
import PurchaseOrders from "@/pages/PurchaseOrders";
import SupplierOrderList from "@/pages/SupplierOrderList";
import SupplierAllocation from "@/pages/SupplierAllocation";
import FinancialSummary from "@/pages/FinancialSummary";
import YearlyFinancialSummary from "@/pages/YearlyFinancialSummary";
import AllTimeFinancialSummary from "@/pages/AllTimeFinancialSummary";
import CashierAnalytics from "@/pages/CashierAnalytics";
import CardReconciliation from "@/pages/CardReconciliation";
import CashFlowPlanner from "@/pages/CashFlowPlanner";
import NegativeStockReport from "@/pages/NegativeStockReport";
import Locations from "@/pages/Locations";
import AccountantPack from "@/pages/AccountantPack";
import SupplierDocuments from "@/pages/SupplierDocuments";
import Reports from "@/pages/Reports";
import StockTake from "@/pages/StockTake";
import PrintStocktakeSheets from "@/pages/PrintStocktakeSheets";
import CompanySettings from "@/pages/CompanySettings";
import PrinterSettings from "@/pages/PrinterSettings";
import AccountDiscountSettings from "@/pages/AccountDiscountSettings";
import CustomerLevels from "@/pages/CustomerLevels";
import StockCountList from "@/pages/StockCountList";
import StockCountNew from "@/pages/StockCountNew";
import StockCountSession from "@/pages/StockCountSession";
import StockLabelPrinting from "@/pages/StockLabelPrinting";
import ExpiryManagement from "@/pages/ExpiryManagement";
import AirtimeMachine from "@/pages/AirtimeMachine";
import Expenses from "@/pages/Expenses";
import CashUp from "@/pages/CashUp";
import FlashReconciliation from "@/pages/FlashReconciliation";
import PayrollRuns from "@/pages/PayrollRuns";
import CasualWages from "@/pages/CasualWages";
import Budget from "@/pages/Budget";
import IncomeStatement from "@/pages/IncomeStatement";
import MarkupCoverage from "@/pages/MarkupCoverage";
import PriceSuggestions from "@/pages/PriceSuggestions";
import RaiseAffordability from "@/pages/RaiseAffordability";
import CommissionEngine from "@/pages/CommissionEngine";
import Consignment from "@/pages/Consignment";
import Employees from "@/pages/Employees";
import StaffClock from "@/pages/StaffClock";
import Promotions from "@/pages/Promotions";
import GiftVouchers from "@/pages/GiftVouchers";
import PromoFlyers from "@/pages/PromoFlyers";
import StoreNotice from "@/pages/StoreNotice";
import DayEndReport from "@/pages/DayEndReport";
import SmartDashboard from "@/pages/SmartDashboard";
import DailyTotals from "@/pages/DailyTotals";
import DailyTransactionsReport from "@/pages/DailyTransactionsReport";
import VoucherAuditReport from "@/pages/VoucherAuditReport";
import StockLevelBulkEditor from "@/pages/StockLevelBulkEditor";
import BackupPage from "@/pages/BackupPage";
import UserManagement from "@/pages/UserManagement";
import LiveMonitor from "@/pages/LiveMonitor";
import InternalSplits from "@/pages/InternalSplits";
import AuditLog from "@/pages/AuditLog";
import AuditPack from "@/pages/AuditPack";
import BalanceSheet from "@/pages/BalanceSheet";
import SmartProcurement from "@/pages/SmartProcurement";
import VatReport from "@/pages/VatReport";
import SalesAudit from "@/pages/SalesAudit";
import PriceChanges from "@/pages/PriceChanges";
import FlyerRebrander from "@/pages/FlyerRebrander";
import AssetManager from "@/pages/AssetManager";
import UnitPriceCalculator from "@/pages/UnitPriceCalculator";
import ProductImageUpload from "@/pages/ProductImageUpload";
import SecuritySettings from "@/pages/SecuritySettings";
import AIFeatures from "@/pages/AIFeatures";
import AgrinetSync from "@/pages/AgrinetSync";
import ProductAssistant from "@/pages/ProductAssistant";
import SelfUseAudit from "@/pages/SelfUseAudit";
import DocumentLookup from "@/pages/DocumentLookup";
import HistoricalInvoices from "@/pages/HistoricalInvoices";
import DataIntegrity from "@/pages/DataIntegrity";
import DataQualityCheck from "@/pages/DataQualityCheck";
import CustomerEmailPreferences from "@/pages/CustomerEmailPreferences";
import Notes from "@/pages/Notes";
import NewArrivalsPromoter from "@/pages/NewArrivalsPromoter";
import GrvMobileUpload from "@/pages/GrvMobileUpload";
import MobileLayout from "@/pages/mobile/MobileLayout";
import MobileLogin from "@/pages/mobile/MobileLogin";
import MobileHome from "@/pages/mobile/MobileHome";
import MobilePriceCheck from "@/pages/mobile/MobilePriceCheck";
import { getWarehouseSession } from "@/lib/warehouseSession";
import Layout from "@/components/Layout";
import { Toaster } from "@/components/ui/sonner";
import { VatProvider } from "@/context/VatContext";
import { Loader2 } from "lucide-react";

// Website (Public) Pages
import WebsiteHome from "@/pages/website/WebsiteHome";
import WebsiteProducts from "@/pages/website/WebsiteProducts";
import WebsiteContact from "@/pages/website/WebsiteContact";
import WebsitePromotions from "@/pages/website/WebsitePromotions";
import WebsiteAbout from "@/pages/website/WebsiteAbout";
import { ShopProvider } from "@/shop/ShopContext";
import ShopLayout from "@/shop/ShopLayout";
import ShopHome from "@/shop/ShopHome";
import ShopCatalog from "@/shop/ShopCatalog";
import ShopProduct from "@/shop/ShopProduct";
import ShopCheckout from "@/shop/ShopCheckout";
import ShopLogin from "@/shop/ShopLogin";
import ShopAccount, { ShopOrderDone } from "@/shop/ShopAccount";
import { ShopAuthCallback, ShopResetPassword } from "@/shop/ShopAuthPages";
import OnlineOrders from "@/pages/OnlineOrders";

const ShopRoutes = () => {
  const location = useLocation();
  return (
    <ShopProvider>
      {location.hash?.includes("session_id=") ? (
        <ShopAuthCallback />
      ) : (
        <Routes>
          <Route element={<ShopLayout />}>
            <Route path="/shop" element={<ShopHome />} />
            <Route path="/shop/products" element={<ShopCatalog />} />
            <Route path="/shop/p/:id" element={<ShopProduct />} />
            <Route path="/shop/checkout" element={<ShopCheckout />} />
            <Route path="/shop/login" element={<ShopLogin />} />
            <Route path="/shop/reset-password" element={<ShopResetPassword />} />
            <Route path="/shop/account" element={<ShopAccount />} />
            <Route path="/shop/order/:id" element={<ShopOrderDone />} />
            <Route path="/shop/*" element={<Navigate to="/shop" replace />} />
          </Route>
        </Routes>
      )}
    </ShopProvider>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading, canAccess } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Check if user can access this route
  if (!canAccess(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Routes Component (needs to be inside AuthProvider)
const AppRoutes = () => {
  const { isAuthenticated, loading, setupRequired } = useAuth();
  const location = useLocation();

  // Check if current path is a public website route OR the root path for non-authenticated users
  const isPublicRoute = location.pathname.startsWith("/site");
  const isMobileHandoffRoute = location.pathname.startsWith("/grv/mobile-upload");
  const isMobileWarehouseRoute = location.pathname.startsWith("/mobile");
  const isLoginRoute = location.pathname === "/login";
  const isRootPath = location.pathname === "/";

  // Online shop — public, own shopper session (never the staff login)
  if (location.pathname === "/shop" || location.pathname.startsWith("/shop/")) {
    return <ShopRoutes />;
  }

  // For public website routes, render without auth check
  if (isPublicRoute) {
    return (
      <Routes>
        <Route path="/site" element={<WebsiteHome />} />
        <Route path="/site/products" element={<WebsiteProducts />} />
        <Route path="/site/contact" element={<WebsiteContact />} />
        <Route path="/site/promotions" element={<WebsitePromotions />} />
        <Route path="/site/about" element={<WebsiteAbout />} />
      </Routes>
    );
  }

  // Phone handoff page — no auth, token in URL is the credential
  if (isMobileHandoffRoute) {
    return (
      <Routes>
        <Route path="/grv/mobile-upload/:token" element={<GrvMobileUpload />} />
      </Routes>
    );
  }

  // Warehouse mobile module — completely sandboxed shell (own PIN session).
  // Renders WITHOUT the desktop <Layout> so employees see only the phone UI.
  if (isMobileWarehouseRoute) {
    return (
      <Routes>
        <Route path="/mobile/login" element={<MobileLogin />} />
        <Route element={<MobileLayout />}>
          <Route path="/mobile" element={<MobileHome />} />
          <Route path="/mobile/bin-assign" element={<BinAssign />} />
          <Route path="/mobile/stock-count" element={<StockCountList />} />
          <Route path="/mobile/stock-count/new" element={<StockCountNew />} />
          <Route path="/mobile/stock-count/session/:code" element={<StockCountSession />} />
          <Route path="/mobile/price-check" element={<MobilePriceCheck />} />
          <Route path="/mobile/*" element={<Navigate to="/mobile" replace />} />
        </Route>
      </Routes>
    );
  }

  // Warehouse-PIN users must not roam the desktop app. If a warehouse
  // session exists but there is no admin auth token, kick every non-/mobile
  // URL back to /mobile.
  if (getWarehouseSession() && !localStorage.getItem("auth_token")) {
    return <Navigate to="/mobile" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // If not authenticated and visiting root path, redirect to the login screen
  if (!isAuthenticated || setupRequired) {
    // Redirect root to staff login (customers can still visit /site/* directly)
    if (isRootPath) {
      return <Navigate to="/login" replace />;
    }

    // Anything other than /login → bounce to /login so the URL actually changes.
    // Without this, the URL stays on a protected route (e.g. /users), and after
    // a successful re-login the same protected page mounts again, refires its
    // admin-only fetch, and (if the call 401s for any reason) the global axios
    // interceptor logs the user back out — creating a login loop.
    if (!isLoginRoute) {
      return <Navigate to="/login" replace />;
    }

    // Show login page for /login
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        
        {/* Cashier accessible routes */}
        <Route path="/" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
        <Route path="/sales" element={<ProtectedRoute><Sales /></ProtectedRoute>} />
        <Route path="/cashup" element={<ProtectedRoute><CashUp /></ProtectedRoute>} />
        <Route path="/flash-reconciliation" element={<ProtectedRoute><FlashReconciliation /></ProtectedRoute>} />
        <Route path="/payroll-runs" element={<ProtectedRoute><PayrollRuns /></ProtectedRoute>} />
        <Route path="/casual-wages" element={<ProtectedRoute><CasualWages /></ProtectedRoute>} />
        <Route path="/print-stocktake" element={<ProtectedRoute><PrintStocktakeSheets /></ProtectedRoute>} />
        
        {/* Admin only routes */}
        <Route path="/quotations" element={<ProtectedRoute adminOnly><Quotations /></ProtectedRoute>} />
        <Route path="/quotation-management" element={<ProtectedRoute adminOnly><QuotationManagement /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
        <Route path="/products" element={<ProtectedRoute adminOnly><Products /></ProtectedRoute>} />
        <Route path="/stock-master" element={<ProtectedRoute adminOnly><ProductMaster /></ProtectedRoute>} />
        <Route path="/stock-conversions" element={<ProtectedRoute adminOnly><StockConversions /></ProtectedRoute>} />
        <Route path="/promotions" element={<ProtectedRoute adminOnly><Promotions /></ProtectedRoute>} />
        <Route path="/gift-vouchers" element={<ProtectedRoute adminOnly><GiftVouchers /></ProtectedRoute>} />
        <Route path="/online-orders" element={<ProtectedRoute><OnlineOrders /></ProtectedRoute>} />
        <Route path="/promo-flyers" element={<ProtectedRoute adminOnly><PromoFlyers /></ProtectedRoute>} />
        <Route path="/store-notice" element={<ProtectedRoute adminOnly><StoreNotice /></ProtectedRoute>} />
        <Route path="/reports/day-end" element={<ProtectedRoute adminOnly><DayEndReport /></ProtectedRoute>} />
        <Route path="/customers" element={<ProtectedRoute adminOnly><CustomersV2 /></ProtectedRoute>} />
        <Route path="/customers-old" element={<ProtectedRoute adminOnly><Customers /></ProtectedRoute>} />
        <Route path="/customer-email-preferences" element={<ProtectedRoute adminOnly><CustomerEmailPreferences /></ProtectedRoute>} />
        <Route path="/suppliers" element={<ProtectedRoute adminOnly><Suppliers /></ProtectedRoute>} />
        <Route path="/reorder" element={<ProtectedRoute adminOnly><ReorderReport /></ProtectedRoute>} />
        <Route path="/negative-stock" element={<ProtectedRoute adminOnly><NegativeStockReport /></ProtectedRoute>} />
        <Route path="/warehouse/locations" element={<ProtectedRoute adminOnly><Locations /></ProtectedRoute>} />
        <Route path="/accountant-pack" element={<ProtectedRoute adminOnly><AccountantPack /></ProtectedRoute>} />
        <Route path="/supplier-documents" element={<ProtectedRoute adminOnly><SupplierDocuments /></ProtectedRoute>} />
        <Route path="/petty-cash" element={<ProtectedRoute adminOnly><PettyCashReport /></ProtectedRoute>} />
        <Route path="/grv" element={<ProtectedRoute adminOnly><StockReceiving /></ProtectedRoute>} />
        <Route path="/bin-assign" element={<ProtectedRoute><BinAssign /></ProtectedRoute>} />
        <Route path="/buying-group-report" element={<ProtectedRoute adminOnly><BuyingGroupReport /></ProtectedRoute>} />
        <Route path="/print-station" element={<ProtectedRoute adminOnly><PrintStation /></ProtectedRoute>} />
        <Route path="/internal-supplies" element={<ProtectedRoute adminOnly><InternalSupplies /></ProtectedRoute>} />
        <Route path="/makro-direct" element={<ProtectedRoute adminOnly><MakroDirect /></ProtectedRoute>} />
        <Route path="/makro-direct/:orderId" element={<ProtectedRoute adminOnly><MakroDirect /></ProtectedRoute>} />
        <Route path="/stock-orders" element={<ProtectedRoute adminOnly><PurchaseOrders /></ProtectedRoute>} />
        <Route path="/supplier-order-list" element={<ProtectedRoute adminOnly><SupplierOrderList /></ProtectedRoute>} />
        <Route path="/supplier-allocation" element={<ProtectedRoute adminOnly><SupplierAllocation /></ProtectedRoute>} />
        <Route path="/import" element={<ProtectedRoute adminOnly><DataImport /></ProtectedRoute>} />
        <Route path="/financial-summary" element={<ProtectedRoute adminOnly><FinancialSummary /></ProtectedRoute>} />
        <Route path="/yearly-summary" element={<ProtectedRoute adminOnly><YearlyFinancialSummary /></ProtectedRoute>} />
        <Route path="/yearly-summary/all-time" element={<ProtectedRoute adminOnly><AllTimeFinancialSummary /></ProtectedRoute>} />
        <Route path="/yearly-financial/all-time" element={<ProtectedRoute adminOnly><AllTimeFinancialSummary /></ProtectedRoute>} />
        <Route path="/reports/cashier-analytics" element={<ProtectedRoute adminOnly><CashierAnalytics /></ProtectedRoute>} />
        <Route path="/reports/card-reconciliation" element={<ProtectedRoute adminOnly><CardReconciliation /></ProtectedRoute>} />
        <Route path="/cash-flow-planner" element={<ProtectedRoute adminOnly><CashFlowPlanner /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute adminOnly><Reports /></ProtectedRoute>} />
        <Route path="/reports/daily-transactions" element={<ProtectedRoute adminOnly><DailyTransactionsReport /></ProtectedRoute>} />
        <Route path="/reports/voucher-audit" element={<ProtectedRoute adminOnly><VoucherAuditReport /></ProtectedRoute>} />
        <Route path="/reports/limpopo-insights" element={<ProtectedRoute adminOnly><LimpopoInsights /></ProtectedRoute>} />
        <Route path="/stock-take" element={<ProtectedRoute adminOnly><StockTake /></ProtectedRoute>} />
        <Route path="/expiry" element={<ProtectedRoute adminOnly><ExpiryManagement /></ProtectedRoute>} />
        <Route path="/airtime-machine" element={<ProtectedRoute adminOnly><AirtimeMachine /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute adminOnly><Expenses /></ProtectedRoute>} />
        <Route path="/budget" element={<ProtectedRoute adminOnly><Budget /></ProtectedRoute>} />
        <Route path="/income-statement" element={<ProtectedRoute adminOnly><IncomeStatement /></ProtectedRoute>} />
        <Route path="/markup-coverage" element={<ProtectedRoute adminOnly><MarkupCoverage /></ProtectedRoute>} />
        <Route path="/price-suggestions" element={<ProtectedRoute adminOnly><PriceSuggestions /></ProtectedRoute>} />
        <Route path="/raise-affordability" element={<ProtectedRoute adminOnly><RaiseAffordability /></ProtectedRoute>} />
        <Route path="/commission" element={<ProtectedRoute adminOnly><CommissionEngine /></ProtectedRoute>} />
        <Route path="/consignment" element={<ProtectedRoute adminOnly><Consignment /></ProtectedRoute>} />
        <Route path="/employees" element={<ProtectedRoute adminOnly><Employees /></ProtectedRoute>} />
        <Route path="/staff-clock" element={<ProtectedRoute><StaffClock /></ProtectedRoute>} />
        <Route path="/smart-dashboard" element={<ProtectedRoute adminOnly><SmartDashboard /></ProtectedRoute>} />
        <Route path="/daily-totals" element={<ProtectedRoute adminOnly><DailyTotals /></ProtectedRoute>} />
        <Route path="/stock-level-editor" element={<ProtectedRoute adminOnly><StockLevelBulkEditor /></ProtectedRoute>} />
        <Route path="/product-images" element={<ProtectedRoute adminOnly><ProductImageUpload /></ProtectedRoute>} />
        <Route path="/ai-features" element={<ProtectedRoute adminOnly><AIFeatures /></ProtectedRoute>} />
        <Route path="/agrinet-sync" element={<ProtectedRoute adminOnly><AgrinetSync /></ProtectedRoute>} />
        <Route path="/product-assistant" element={<ProtectedRoute><ProductAssistant /></ProtectedRoute>} />
        <Route path="/backup" element={<ProtectedRoute adminOnly><BackupPage /></ProtectedRoute>} />
        <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
        <Route path="/live-monitor" element={<ProtectedRoute adminOnly><LiveMonitor /></ProtectedRoute>} />
        <Route path="/internal-splits" element={<ProtectedRoute adminOnly><InternalSplits /></ProtectedRoute>} />
        <Route path="/audit-log" element={<ProtectedRoute adminOnly><AuditLog /></ProtectedRoute>} />
        <Route path="/audit-pack" element={<ProtectedRoute adminOnly><AuditPack /></ProtectedRoute>} />
        <Route path="/balance-sheet" element={<ProtectedRoute adminOnly><BalanceSheet /></ProtectedRoute>} />
        <Route path="/smart-procurement" element={<ProtectedRoute adminOnly><SmartProcurement /></ProtectedRoute>} />
        <Route path="/vat-report" element={<ProtectedRoute adminOnly><VatReport /></ProtectedRoute>} />
        <Route path="/sales-audit" element={<ProtectedRoute adminOnly><SalesAudit /></ProtectedRoute>} />
        <Route path="/price-changes" element={<ProtectedRoute adminOnly><PriceChanges /></ProtectedRoute>} />
        <Route path="/flyer-rebrander" element={<ProtectedRoute adminOnly><FlyerRebrander /></ProtectedRoute>} />
        <Route path="/asset-manager" element={<ProtectedRoute adminOnly><AssetManager /></ProtectedRoute>} />
        <Route path="/unit-price" element={<ProtectedRoute><UnitPriceCalculator /></ProtectedRoute>} />
        <Route path="/self-use-audit" element={<ProtectedRoute><SelfUseAudit /></ProtectedRoute>} />
        <Route path="/document-lookup" element={<ProtectedRoute><DocumentLookup /></ProtectedRoute>} />
        <Route path="/historical-invoices" element={<ProtectedRoute adminOnly><HistoricalInvoices /></ProtectedRoute>} />
        <Route path="/data-integrity" element={<ProtectedRoute adminOnly><DataIntegrity /></ProtectedRoute>} />
        <Route path="/data-quality" element={<ProtectedRoute adminOnly><DataQualityCheck /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute adminOnly><CompanySettings /></ProtectedRoute>} />
        <Route path="/settings/account-discounts" element={<ProtectedRoute adminOnly><AccountDiscountSettings /></ProtectedRoute>} />
        <Route path="/settings/customer-levels" element={<ProtectedRoute adminOnly><CustomerLevels /></ProtectedRoute>} />
        <Route path="/stock-count" element={<ProtectedRoute><StockCountList /></ProtectedRoute>} />
        <Route path="/stock-count/new" element={<ProtectedRoute><StockCountNew /></ProtectedRoute>} />
        <Route path="/stock-count/session/:code" element={<ProtectedRoute><StockCountSession /></ProtectedRoute>} />
        <Route path="/settings/security" element={<ProtectedRoute adminOnly><SecuritySettings /></ProtectedRoute>} />
        <Route path="/printer-settings" element={<ProtectedRoute adminOnly><PrinterSettings /></ProtectedRoute>} />
        <Route path="/stock-labels" element={<ProtectedRoute adminOnly><StockLabelPrinting /></ProtectedRoute>} />
        <Route path="/notes" element={<ProtectedRoute><Notes /></ProtectedRoute>} />
        <Route path="/new-arrivals-promo" element={<ProtectedRoute adminOnly><NewArrivalsPromoter /></ProtectedRoute>} />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <VatProvider>
            <AppRoutes />
          </VatProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
